import React, { useState } from 'react';
import {
  Table,
  Card,
  DatePicker,
  Button,
  message,
  Space,
  Row,
  Col,
  Statistic,
  Typography,
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { aulaDadaService } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

const RelatorioMensal = () => {
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState([]);
  const [mesSelecionado, setMesSelecionado] = useState(dayjs());
  const [totalizadores, setTotalizadores] = useState({
    totalAulas: 0,
    totalHoras: 0,
    totalValor: 0,
  });

  const fetchRelatorio = async () => {
    setLoading(true);
    try {
      const mes = mesSelecionado.month() + 1;
      const ano = mesSelecionado.year();

      const response = await aulaDadaService.relatorioMensal({ mes, ano });
      setRelatorio(response.data);
      calcularTotais(response.data);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao gerar relatório';
      message.error(errorMsg);
    }
    setLoading(false);
  };

  const calcularTotais = (data) => {
    const totais = data.reduce(
      (acc, item) => {
        acc.totalAulas += item.total_aulas || 0;
        acc.totalHoras += item.total_horas || 0;
        acc.totalValor += item.valor_total || 0;
        return acc;
      },
      { totalAulas: 0, totalHoras: 0, totalValor: 0 }
    );
    setTotalizadores(totais);
  };

  const exportarCSV = () => {
    if (relatorio.length === 0) {
      message.warning('Nenhum dado para exportar');
      return;
    }

    const headers = [
      'Professor',
      'Total Aulas',
      'Aulas Validadas',
      'Aulas Pendentes',
      'Aulas Rejeitadas',
      'Total Horas',
      'Valor/Hora',
      'Valor Total',
    ];

    const rows = relatorio.map((item) => [
      item.nome_professor,
      item.total_aulas,
      item.aulas_validadas,
      item.aulas_pendentes,
      item.aulas_rejeitadas,
      item.total_horas.toFixed(2),
      item.valor_hora?.toFixed(2) || '0.00',
      item.valor_total.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `relatorio_${mesSelecionado.format('MM-YYYY')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('Relatório exportado com sucesso');
  };

  const columns = [
    {
      title: 'Professor',
      dataIndex: 'nome_professor',
      key: 'nome_professor',
      width: 200,
    },
    {
      title: 'Total Aulas',
      dataIndex: 'total_aulas',
      key: 'total_aulas',
      align: 'center',
      width: 120,
    },
    {
      title: 'Validadas',
      dataIndex: 'aulas_validadas',
      key: 'aulas_validadas',
      align: 'center',
      width: 100,
      render: (val) => <span style={{ color: '#52c41a' }}>{val}</span>,
    },
    {
      title: 'Pendentes',
      dataIndex: 'aulas_pendentes',
      key: 'aulas_pendentes',
      align: 'center',
      width: 100,
      render: (val) => <span style={{ color: '#faad14' }}>{val}</span>,
    },
    {
      title: 'Rejeitadas',
      dataIndex: 'aulas_rejeitadas',
      key: 'aulas_rejeitadas',
      align: 'center',
      width: 100,
      render: (val) => <span style={{ color: '#f5222d' }}>{val}</span>,
    },
    {
      title: 'Total Horas',
      dataIndex: 'total_horas',
      key: 'total_horas',
      align: 'right',
      width: 120,
      render: (val) => val.toFixed(2),
    },
    {
      title: 'Valor/Hora',
      dataIndex: 'valor_hora',
      key: 'valor_hora',
      align: 'right',
      width: 120,
      render: (val) => `R$ ${val ? val.toFixed(2) : '0.00'}`,
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_total',
      key: 'valor_total',
      align: 'right',
      width: 150,
      render: (val) => (
        <strong style={{ color: '#1890ff' }}>R$ {val.toFixed(2)}</strong>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Relatório Mensal de Pagamentos</Title>

      <Card style={{ marginBottom: 24 }}>
        <Space>
          <DatePicker
            picker="month"
            format="MM/YYYY"
            value={mesSelecionado}
            onChange={(date) => setMesSelecionado(date)}
            placeholder="Selecione o mês"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchRelatorio}
            loading={loading}
          >
            Gerar Relatório
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportarCSV}
            disabled={relatorio.length === 0}
          >
            Exportar CSV
          </Button>
        </Space>
      </Card>

      {relatorio.length > 0 && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total de Aulas"
                  value={totalizadores.totalAulas}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total de Horas"
                  value={totalizadores.totalHoras.toFixed(2)}
                  suffix="h"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Valor Total a Pagar"
                  value={totalizadores.totalValor.toFixed(2)}
                  prefix="R$"
                  valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={relatorio}
            loading={loading}
            rowKey="id_equipe"
            pagination={false}
            bordered
            summary={(pageData) => {
              const totalAulas = pageData.reduce(
                (sum, r) => sum + r.total_aulas,
                0
              );
              const totalHoras = pageData.reduce(
                (sum, r) => sum + r.total_horas,
                0
              );
              const totalValor = pageData.reduce(
                (sum, r) => sum + r.valor_total,
                0
              );

              return (
                <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                  <Table.Summary.Cell>
                    <strong>TOTAL</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="center">
                    <strong>{totalAulas}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                  <Table.Summary.Cell align="right">
                    <strong>{totalHoras.toFixed(2)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell />
                  <Table.Summary.Cell align="right">
                    <strong style={{ color: '#1890ff' }}>
                      R$ {totalValor.toFixed(2)}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </>
      )}
    </div>
  );
};

export default RelatorioMensal;
